import React, { useMemo, useState } from 'react';
import type { MenuProps } from 'antd';
import { Layout as AntLayout, Menu, Button, Dropdown, Avatar, Space, Drawer, Grid } from 'antd';
import {
  DashboardOutlined,
  ShopOutlined,
  AppstoreOutlined,
  MessageOutlined,
  StarOutlined,
  BarChartOutlined,
  BellOutlined,
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';
import NotificationBellDropdown from './NotificationBellDropdown';
import VendorBreadcrumbs from './VendorBreadcrumbs';

const { Header, Sider, Content } = AntLayout;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const screens = Grid.useBreakpoint();
  const isLargeScreen = !!screens.lg;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const handleMenuClick = (key: string) => {
    navigate(key);
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path === '/dashboard') return ['/dashboard'];
    if (path.startsWith('/stores')) {
      if (path === '/stores/create') return ['/stores/create'];
      return ['/stores'];
    }
    if (path.startsWith('/listings')) {
      if (path === '/listings/create') return ['/listings/create'];
      return ['/listings'];
    }
    return [path];
  };

  const getOpenKeys = () => {
    const path = location.pathname;
    if (path.startsWith('/stores')) return ['stores'];
    if (path.startsWith('/listings')) return ['listings'];
    return [];
  };

  /** Desktop collapse; mobile drawer always shows expanded chrome */
  const sidebarCollapsed = collapsed && !mobileMenuOpen;

  const menuItems = useMemo<MenuProps['items']>(() => {
    const dashboard = { key: '/dashboard', icon: <DashboardOutlined />, label: 'Dashboard' };
    const stores = {
      key: 'stores',
      icon: <ShopOutlined />,
      label: 'Stores',
      popupClassName: 'vendor-sider-submenu-popup',
      children: [
        { key: '/stores', icon: <ShopOutlined />, label: 'My stores' },
        { key: '/stores/create', icon: <PlusOutlined />, label: 'Create store' },
      ],
    };
    const listings = {
      key: 'listings',
      icon: <AppstoreOutlined />,
      label: 'Listings',
      popupClassName: 'vendor-sider-submenu-popup',
      children: [
        { key: '/listings', icon: <AppstoreOutlined />, label: 'My listings' },
        { key: '/listings/create', icon: <PlusOutlined />, label: 'Create listing' },
      ],
    };
    const leads = { key: '/leads', icon: <MessageOutlined />, label: 'Leads' };
    const reviews = { key: '/reviews', icon: <StarOutlined />, label: 'Reviews' };
    const analytics = { key: '/analytics', icon: <BarChartOutlined />, label: 'Analytics' };
    const notifications = { key: '/notifications', icon: <BellOutlined />, label: 'Notifications' };

    if (sidebarCollapsed) {
      return [dashboard, stores, listings, leads, reviews, analytics, notifications];
    }

    return [
      { type: 'group', label: 'Overview', children: [dashboard] },
      { type: 'group', label: 'Stores & listings', children: [stores, listings] },
      { type: 'group', label: 'Customers & insights', children: [leads, reviews, analytics, notifications] },
    ];
  }, [sidebarCollapsed]);

  const siderContent = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center justify-center border-b border-[#0171BF]/12 bg-white px-2 py-3 sm:px-4 sm:py-5">
        {sidebarCollapsed ? (
          <img
            src="/toc.png"
            alt="The Old Cart"
            className="h-9 w-9 object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        ) : (
          <img
            src="/logo.png"
            alt="The Old Cart"
            className="h-9 w-auto max-h-10 max-w-[11rem] object-contain object-center sm:max-h-11 sm:max-w-[13rem]"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
            }}
          />
        )}
      </div>

      <div className="vendor-sider flex min-h-0 flex-1 flex-col overflow-x-hidden bg-[#0171BF]">
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-1 py-3 sm:px-2 sm:py-4">
          <Menu
            theme="light"
            mode="inline"
            inlineCollapsed={isLargeScreen ? collapsed : false}
            selectedKeys={getSelectedKeys()}
            defaultOpenKeys={getOpenKeys()}
            items={menuItems}
            onClick={({ key }) => handleMenuClick(key)}
            className="vendor-sider-menu !border-0 !bg-transparent"
            getPopupContainer={() => document.body}
            style={{ backgroundColor: 'transparent' }}
          />
        </div>
      </div>
    </div>
  );

  return (
    <AntLayout className="min-h-screen bg-gray-50">
      {/* Desktop Sidebar - Fixed 100vh */}
      <div className="hidden lg:block fixed left-0 top-0 bottom-0 z-20">
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          className="vendor-layout-sider h-screen !max-w-full !overflow-x-hidden !border-r !border-[#015a9e] shadow-xl !bg-[#0171BF] [&_.ant-layout-sider-children]:flex [&_.ant-layout-sider-children]:h-full [&_.ant-layout-sider-children]:min-h-0 [&_.ant-layout-sider-children]:min-w-0 [&_.ant-layout-sider-children]:flex-col [&_.ant-layout-sider-children]:overflow-x-hidden"
          width={300}
          collapsedWidth={64}
          style={{
            overflow: 'hidden',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            transition: 'width 0.2s ease, min-width 0.2s ease',
          }}
        >
          {siderContent}
        </Sider>
      </div>

      {/* Mobile Drawer */}
      <Drawer
        title={null}
        closable={false}
        placement="left"
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        rootClassName="lg:hidden vendor-mobile-drawer"
        width="min(20rem, 92vw)"
        styles={{ body: { padding: 0 } }}
      >
        {siderContent}
      </Drawer>

      <AntLayout
        className={[
          'flex min-h-screen min-w-0 flex-1 flex-col bg-gray-50',
          collapsed ? 'lg:ml-[64px]' : 'lg:ml-[300px]',
        ].join(' ')}
        style={{
          transition: 'margin-left 0.2s ease',
          minHeight: '100vh',
        }}
      >
        <Header className="sticky top-0 z-[100] flex h-16 min-h-16 shrink-0 items-center justify-between gap-2 border-b border-gray-200 bg-white/95 px-3 shadow-sm backdrop-blur-sm sm:px-4 lg:px-6 ps-[max(0.75rem,env(safe-area-inset-left))] pe-[max(0.75rem,env(safe-area-inset-right))] pt-[max(0.5rem,env(safe-area-inset-top))] lg:pt-0">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <Button
              type="text"
              icon={
                isLargeScreen ? (
                  collapsed ? (
                    <MenuUnfoldOutlined />
                  ) : (
                    <MenuFoldOutlined />
                  )
                ) : (
                  <MenuUnfoldOutlined />
                )
              }
              onClick={() => {
                if (isLargeScreen) setCollapsed((c) => !c);
                else setMobileMenuOpen(true);
              }}
              title={
                isLargeScreen
                  ? collapsed
                    ? 'Show side menu'
                    : 'Hide side menu'
                  : 'Open menu'
              }
              aria-label={
                isLargeScreen
                  ? collapsed
                    ? 'Expand side menu'
                    : 'Collapse side menu'
                  : 'Open menu'
              }
              className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-gray-600 hover:bg-blue-50 hover:text-[#0171BF]"
            />
          </div>

          <div className="flex shrink-0 items-center justify-end gap-1 sm:gap-2">
            <Space size={4} wrap={false} className="flex items-center">
              <NotificationBellDropdown />

              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'profile',
                      icon: <UserOutlined />,
                      label: 'Your profile',
                      onClick: () => navigate('/profile'),
                    },
                    {
                      key: 'settings',
                      icon: <SettingOutlined />,
                      label: 'Settings',
                      onClick: () => navigate('/settings'),
                    },
                    { type: 'divider' as const },
                    {
                      key: 'logout',
                      icon: <LogoutOutlined />,
                      label: 'Sign out',
                      onClick: handleLogout,
                      danger: true,
                    },
                  ],
                }}
                placement="bottomRight"
                trigger={['click']}
              >
                <button
                  type="button"
                  title="Account menu"
                  aria-label="Account menu"
                  className="flex min-h-11 max-w-[11rem] cursor-pointer items-center gap-2 rounded-xl px-1.5 py-1 text-left transition-colors hover:bg-gray-50 sm:max-w-none sm:px-2"
                >
                  <Avatar
                    size={36}
                    src={user?.profile?.avatar}
                    className="shrink-0 shadow-sm ring-2 ring-white"
                    style={{ backgroundColor: '#0171BF' }}
                  >
                    {(user?.profile?.firstName?.[0] || 'U').toUpperCase()}
                  </Avatar>
                  <div className="min-w-0 flex-1 max-sm:hidden md:block">
                    <p className="truncate text-sm font-semibold leading-tight text-gray-900">
                      {user?.profile?.firstName} {user?.profile?.lastName}
                    </p>
                    <p className="truncate text-xs text-gray-500">Your account</p>
                  </div>
                </button>
              </Dropdown>
            </Space>
          </div>
        </Header>

        <Content className="flex-1 min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-50 via-white to-slate-50/80 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] ps-[max(1rem,env(safe-area-inset-left))] pe-[max(1rem,env(safe-area-inset-right))] sm:py-5 sm:ps-[max(1.25rem,env(safe-area-inset-left))] sm:pe-[max(1.25rem,env(safe-area-inset-right))] lg:py-6 lg:ps-[max(1.5rem,env(safe-area-inset-left))] lg:pe-[max(1.5rem,env(safe-area-inset-right))]">
          <div className="mx-auto w-full max-w-7xl">
            <VendorBreadcrumbs />
            {children}
          </div>
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
